import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton, keyframes, useMediaQuery, useTheme } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { useNotification } from "@/contexts/notification-context";
import { getMediaType } from "@/helpers/file-helpers";
import useAudioRecorder from "@/hooks/use-audio-recorder";
import useCreateMedia from "@/hooks/requests/message/use-create-media";
import useCreateMessage from "@/hooks/requests/message/use-create-message";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useLoading from "@/hooks/use-loading";
import usePopOver from "@/hooks/use-popover";
import { IChat } from "@/interfaces/chat.interfaces";
import { IMessageInput } from "@/interfaces/message.interface";
import IUser from "@/interfaces/user.interface";
import { CurrentChatInputMessages } from "@/messages/chat/current-chat-input.messages";

import { MediaPopover } from "./midiaPopover";
import { buildMediaFormData, buildMessageToSend, buildMessageWithMediaId } from "../../helpers";
import { StyledTextarea } from "../../styles";

type Props = {
  messages: CurrentChatInputMessages;
  user: IUser;
  chat: IChat;
  onSelectFile: (file: File | null) => void;
  selectedFile: File | null;
  chatInputEnabled: boolean;
};

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const formatDuration = (sec: number) => {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

function ChatInput({ messages, chat, user, onSelectFile, selectedFile, chatInputEnabled }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [text, setText] = useState("");
  const { create: sendMessage } = useCreateMessage();
  const { createMedia } = useCreateMedia();
  const { currentChatInputPlaceholder } = messages;
  const { handleSendNewMessage, custWhatsPlatform, activeChatPlatformId } = useNotification();
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IChat>();
  const customerId = useGetCustomerId();
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const recorder = useAudioRecorder();

  const platformId = activeChatPlatformId;

  const {
    startLoading: startMessageLoading,
    endLoading: endMessageLoading,
    isLoading: isMessageLoading,
    loadingIcon: messageLoadingIcon,
  } = useLoading({ loadingIconColor: "primary" });

  const {
    startLoading: startMediaLoading,
    endLoading: endMediaLoading,
    isLoading: isMediaLoading,
    loadingIcon: mediaLoadingIcon,
  } = useLoading({ loadingIconColor: "primary" });

  const customerExternalId = custWhatsPlatform?.customerExternalId;
  const customerPlatformId = custWhatsPlatform?.id;

  const sendMessageHandler = async () => {
    try {
      setIsSendingMessage(true);
      if (!customerId || !customerExternalId || !customerPlatformId) return;
      if (!platformId) return;

      startMessageLoading();

      const formData: IMessageInput = buildMessageToSend(
        chat,
        user,
        text,
        customerExternalId,
        chat.contactId,
        platformId,
        customerPlatformId
      );

      const newMessage = await sendMessage({ formData });

      newMessage.customerExternalId = formData.fromId;

      await handleSendNewMessage(chat.conversationId, newMessage);
    } finally {
      setIsSendingMessage(false);
      setText("");
      endMessageLoading();

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const sendMediaHandler = async () => {
    if (!selectedFile || !customerPlatformId) return;
    await uploadAndSendMedia(selectedFile, "auto");
    onSelectFile(null);
  };

  const uploadAndSendMedia = async (file: File, mediaTypeOverride: "auto" | "audio") => {
    try {
      startMediaLoading();
      const formData = buildMediaFormData(file, customerExternalId);
      if (!formData) return;

      const mediaResponse = await createMedia({ formData });
      if (!mediaResponse || !mediaResponse.id) throw new Error("Media response is missing an ID.");
      const mediaId = mediaResponse.id;
      const mediaType =
        mediaTypeOverride === "audio"
          ? "audio"
          : getMediaType(file);

      if (!platformId) return;

      const messageFormData = buildMessageWithMediaId(
        chat,
        user,
        mediaTypeOverride === "audio" ? "" : text,
        mediaId,
        mediaType,
        customerExternalId,
        chat.contactId,
        platformId,
        customerPlatformId!
      );

      if (!messageFormData) return;

      const newMessage = await sendMessage({ formData: messageFormData });
      newMessage.customerExternalId = messageFormData.fromId;

      await handleSendNewMessage(chat.conversationId, newMessage);

      if (mediaTypeOverride !== "audio") setText("");
    } finally {
      endMediaLoading();
    }
  };

  const handleMicClick = async () => {
    await recorder.start();
  };

  const handleStopAndSend = async () => {
    const file = await recorder.stop();
    if (!file) return;
    await uploadAndSendMedia(file, "audio");
  };

  const handleCancelRecording = () => {
    recorder.cancel();
  };

  const handleSendMessage = () => {
    if (selectedFile) {
      sendMediaHandler();
    } else {
      sendMessageHandler();
    }
  };

  const isLoading = isMessageLoading || isMediaLoading;
  const loadingIcon = messageLoadingIcon || mediaLoadingIcon;
  const hasContent = !!text.trim() || !!selectedFile;
  const showSendButton = hasContent;
  const canRecord = chatInputEnabled && !isSendingMessage && !isLoading;

  const renderRightButton = () => {
    if (recorder.isRecording) {
      const btn = (
        <IconButton
          onClick={handleStopAndSend}
          disabled={isLoading}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            p: 1,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "grey.300", color: "grey.500" },
          }}
        >
          {isLoading ? loadingIcon : <SendIcon fontSize="small" />}
        </IconButton>
      );
      return btn;
    }

    if (showSendButton) {
      return isMobile ? (
        <IconButton
          onClick={handleSendMessage}
          disabled={!hasContent || isLoading || isSendingMessage || !chatInputEnabled}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            p: 1,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "grey.300", color: "grey.500" },
          }}
        >
          {isLoading ? loadingIcon : <SendIcon fontSize="small" />}
        </IconButton>
      ) : (
        <MDButton
          variant="contained"
          color="primary"
          onClick={handleSendMessage}
          disabled={!hasContent || isLoading || isSendingMessage || !chatInputEnabled}
        >
          {isLoading ? loadingIcon : <SendIcon />}
        </MDButton>
      );
    }

    // Mic button
    return isMobile ? (
      <IconButton
        onClick={handleMicClick}
        disabled={!canRecord}
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          p: 1,
          "&:hover": { backgroundColor: "primary.dark" },
          "&:disabled": { backgroundColor: "grey.300", color: "grey.500" },
        }}
      >
        <MicIcon fontSize="small" />
      </IconButton>
    ) : (
      <MDButton variant="contained" color="primary" onClick={handleMicClick} disabled={!canRecord}>
        <MicIcon />
      </MDButton>
    );
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={isMobile ? 0.5 : 2}
      p={isMobile ? 0.5 : 2}
    >
      {!recorder.isRecording &&
        (isMobile ? (
          <IconButton
            onClick={(e) => openPopover(e, chat)}
            disabled={!chatInputEnabled}
            sx={{ color: "primary.main", p: 0.5 }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        ) : (
          <MDButton
            variant="outlined"
            color="primary"
            onClick={(e) => openPopover(e, chat)}
            disabled={!chatInputEnabled}
          >
            <AttachFileIcon sx={{ fontSize: "30px" }} />
          </MDButton>
        ))}

      {recorder.isRecording ? (
        <MDBox
          flex={1}
          display="flex"
          alignItems="center"
          gap={1.5}
          px={1}
          sx={{
            backgroundColor: "grey.100",
            borderRadius: 2,
            minHeight: 44,
          }}
        >
          <IconButton onClick={handleCancelRecording} size="small" sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={recorder.isPaused ? recorder.resume : recorder.pause}
            size="small"
            sx={{ color: "primary.main" }}
            aria-label={recorder.isPaused ? "retomar gravação" : "pausar gravação"}
          >
            {recorder.isPaused ? (
              <PlayArrowIcon fontSize="small" />
            ) : (
              <PauseIcon fontSize="small" />
            )}
          </IconButton>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: recorder.isPaused ? "grey.500" : "error.main",
              animation: recorder.isPaused ? "none" : `${pulse} 1s infinite`,
            }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {formatDuration(recorder.durationSec)}
          </MDTypography>
        </MDBox>
      ) : (
        <StyledTextarea
          ref={inputRef}
          placeholder={currentChatInputPlaceholder}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setText(e.target.value)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (text.trim()) {
                handleSendMessage();
              }
            }
          }}
          maxRows={1}
          value={text}
          disabled={!chatInputEnabled || isSendingMessage}
        />
      )}

      {renderRightButton()}

      {selected && (
        <MediaPopover
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          messages={messages}
          onSelectFile={onSelectFile}
        />
      )}
    </MDBox>
  );
}

export default ChatInput;
